import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { differenceInYears } from 'date-fns';
import { html, nothing, svg } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { Permission, Role, SBS_ROLES, SPENDER_ROLES } from '../../../permissions/roles.js';
import type { Person } from '../../entities/person.js';
import { FamilyAPI } from '../FamilyAPI.js';
import {
  FAMILY_NODE_HEIGHT,
  FAMILY_NODE_WIDTH,
  type FamilyGraphLink,
  type FamilyGraphNode,
  type FamilyRelation,
  getFamilyGraph,
} from '../familyGraph.js';
import { type FamilyRelative, isStudentRelative } from '../FamilyRelative.js';
import { type FamilyLinkEdit, RelationshipDialog } from './RelationshipDialog.js';

const RELATIVE_SELECTION = {
  _id: 1,
  vorname: 1,
  nachname: 1,
  debitorennummer: 1,
  roles: 1,
  sorgeberechtigteKinderIDs: 1,
  spendenKinderIDs: 1,
  geburtsDatum: 1,
  klasse: 1,
  schule: 1,
  telefonNr: 1,
  email: 1,
  canOpenProfile: 1,
} as const;

type Relative = NarrowedEntity<FamilyRelative, typeof RELATIVE_SELECTION>;

const RELATION_LABELS: Record<FamilyRelation, string> = {
  self: 'Diese Person',
  sorgeberechtigt: 'Sorgeberechtigt',
  spender: 'Spender',
  kind: 'Kind',
  spendenkind: 'Spendenkind',
  geschwister: 'Geschwister',
  weitereSorgeberechtigte: 'Sorgeberechtigt',
  grosselternteil: 'Großelternteil',
  enkelkind: 'Enkelkind',
  weitere: 'Weitere Beziehung',
};

/** relation, singular, plural - in the order the heading lists them */
const SUMMARY_PARTS: Array<[FamilyRelation, string, string]> = [
  ['grosselternteil', 'Großelternteil', 'Großeltern'],
  ['sorgeberechtigt', 'Sorgeberechtigte', 'Sorgeberechtigte'],
  ['spender', 'Spender', 'Spender'],
  ['geschwister', 'Geschwister', 'Geschwister'],
  ['kind', 'Kind', 'Kinder'],
  ['spendenkind', 'Spendenkind', 'Spendenkinder'],
  ['weitereSorgeberechtigte', 'weitere Sorgeberechtigte', 'weitere Sorgeberechtigte'],
  ['enkelkind', 'Enkelkind', 'Enkelkinder'],
  ['weitere', 'weitere Beziehung', 'weitere Beziehungen'],
];

const hasRole = (relative: Relative, roles: string[]) => relative.roles?.some(role => roles.includes(role)) ?? false;
const ageOf = (relative: Relative) =>
  relative.geburtsDatum ? differenceInYears(new Date(), relative.geburtsDatum) : null;

/**
 * One person on the graph. The server decides what it carries: names and roles always, age and class for students,
 * debitor number and contact details only for staff, the link only where the viewer may open the profile.
 */
const RelationCard = (props: { node: FamilyGraphNode<Relative>; selfAge: Maybe<number> }) => {
  const { member, relation, x, y } = props.node;
  const isSelf = relation === 'self';
  const isStudent = isStudentRelative(member);
  const age = ageOf(member);

  const ageDifference =
    relation === 'geschwister' && age !== null && props.selfAge != null ? age - props.selfAge : null;
  const badge =
    ageDifference !== null
      ? ageDifference > 0
        ? { text: `${ageDifference} J. älter`, className: 'badge-secondary' }
        : { text: ageDifference < 0 ? `${-ageDifference} J. jünger` : 'gleich alt', className: 'badge-ghost' }
      : hasRole(member, [Role.MITARBEITER])
        ? { text: 'Mitarbeiter', className: 'badge-ghost' }
        : hasRole(member, [Role.OLD_SORGEBERECHTIGTER])
          ? { text: 'Ehemalig', className: 'badge-ghost' }
          : null;

  const icon = isStudent
    ? 'icon-[material-symbols--school-outline]'
    : hasRole(member, SBS_ROLES)
      ? 'icon-[material-symbols--shield-person-outline]'
      : hasRole(member, SPENDER_ROLES)
        ? 'icon-[material-symbols--volunteer-activism-outline]'
        : 'icon-[material-symbols--person-outline]';

  const status = hasRole(member, [Role.OLD_SCHUELER])
    ? 'Ehemalig'
    : hasRole(member, [Role.SUS_BEWERBER])
      ? 'Bewerber'
      : 'Keine aktive Klasse';
  const [detail, secondaryDetail] = isStudent
    ? [`${age ?? '?'} Jahre · ${member.klasse ? `Klasse ${member.klasse}` : status}`, member.schule]
    : member.telefonNr || member.email
      ? [member.telefonNr || 'Keine Telefonnummer', member.email]
      : [];

  const name = `${member.vorname ?? ''} ${member.nachname ?? ''}`.trim();
  return html`
    <a
      class="card card-border absolute flex flex-col gap-1 bg-base-100 p-4 text-left ${
        isSelf
          ? 'border-2 border-primary'
          : member.canOpenProfile
            ? 'cursor-pointer transition-colors hover:border-primary'
            : ''
      }"
      href=${member.canOpenProfile ? `/personen/${member._id}` : nothing}
      title=${member.canOpenProfile ? 'Profil öffnen' : nothing}
      style=${styleMap({
        left: `${x}px`,
        top: `${y}px`,
        width: `${FAMILY_NODE_WIDTH}px`,
        height: `${FAMILY_NODE_HEIGHT}px`,
      })}
    >
      <span class="flex min-w-0 items-center gap-2">
        <span class="${icon} size-6 shrink-0 text-base-content/70"></span>
        <span class="truncate font-semibold md:text-lg" title=${name}>${name}</span>
      </span>
      <span class="flex min-w-0 items-center justify-between gap-2">
        <span class="truncate text-sm ${isSelf ? 'font-semibold text-primary' : 'text-base-content/70'}">
          ${RELATION_LABELS[relation]}
        </span>
        ${badge ? html`<span class="badge shrink-0 ${badge.className}">${badge.text}</span>` : nothing}
      </span>
      <span class="truncate tabular-nums"
        >${member.debitorennummer ? `Debitor ${member.debitorennummer}` : nothing}</span
      >
      <span class="truncate" title=${detail ?? nothing}>${detail ?? nothing}</span>
      <span class="truncate text-base-content/70" title=${secondaryDetail ?? nothing}
        >${secondaryDetail ?? nothing}</span
      >
    </a>
  `;
};

/**
 * The family around a person as a graph: Sorgeberechtigte and Spender above, Geschwister beside, Kinder below. Staff
 * see every relation as far as it reaches through persons of their schools; Sorgeberechtigte, students and Spender only
 * the direct neighbours. Whoever may link persons adds relationships here and edits one by clicking its line - the rights of the
 * former invitations on the student tab. Without any relation the card is left out for everybody else.
 */
export const PersonRelations = stateful((props: { personID: string }) => {
  const user = useObservable(() => currentUser<Person>(), []);
  // bumped after an edit - the relations are a query, not a subscription
  const [version, setVersion] = useState(0);
  const [hoveredLink, setHoveredLink] = useState<Maybe<string>>(null);
  const {
    value: relatives,
    loading,
    error,
  } = useQuery(() => FamilyAPI.getPersonRelations(props.personID)(RELATIVE_SELECTION), [props.personID, version]);

  const can = (permission: Permission) => user?.hasPermission(permission, ANY_CONTEXT) ?? false;
  const canAdd = can(Permission.MANUAL_SORGEBERECHTIGTE_CONFIRM) || can(Permission.MANUAL_SPENDER_CONFIRM);
  const canEdit =
    canAdd ||
    can(Permission.TOGGLE_SPENDER_INVITE_TYPE) ||
    can(Permission.REMOVE_SORGEBERECHTIGTE) ||
    can(Permission.REMOVE_SPENDER);

  // no placeholder: most persons have no family here, and a loading card that then vanishes jumps the page twice
  if (loading && !relatives) return nothing;
  const self = relatives?.find(relative => relative._id === props.personID);
  if (error || !relatives || !self) {
    return Collapse({
      heading: html`Beziehungen <span class="ml-2 text-sm font-normal text-error">konnten nicht geladen werden</span>`,
      content: nothing,
      disabled: true,
    });
  }

  const graph = getFamilyGraph(self._id, relatives);
  if (graph.nodes.length < 2 && !canAdd) return nothing;

  const openDialog = async (link?: FamilyLinkEdit) => {
    const changed = await showDialog<boolean>({
      content: (resolve, reject) =>
        RelationshipDialog({ viewed: self, viewedAsChild: isStudentRelative(self), relatives, link, resolve, reject }),
      modalBoxClasses: 'max-w-5xl',
    }).catch(() => false);
    if (changed) setVersion(version + 1);
  };
  const nameOf = (id: string) => {
    const relative = relatives.find(candidate => candidate._id === id);
    return `${relative?.vorname ?? ''} ${relative?.nachname ?? ''}`.trim();
  };
  const linkKey = (link: FamilyGraphLink) => `${link.parentID}>${link.childID}`;
  const editLink = (link: FamilyGraphLink) =>
    openDialog({
      parentID: link.parentID,
      childID: link.childID,
      inviteType: link.spender ? 'Spender' : 'Sorgeberechtigter',
    });
  const hovered = graph.links.find(link => linkKey(link) === hoveredLink);

  const selfAge = ageOf(self);
  const olderSiblings = graph.nodes.filter(
    node => node.relation === 'geschwister' && selfAge !== null && (ageOf(node.member) ?? -1) > selfAge,
  ).length;
  const summary =
    SUMMARY_PARTS.flatMap(([relation, singular, plural]) => {
      const count = graph.nodes.filter(node => node.relation === relation).length;
      if (!count) return [];
      const olderNote = relation === 'geschwister' && olderSiblings ? ` (${olderSiblings} älter)` : '';
      return [`${count} ${count === 1 ? singular : plural}${olderNote}`];
    }).join(' · ') || 'Keine Beziehungen hinterlegt';

  return Collapse({
    defaultOpen: true,
    heading: html`
      <span class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span>Beziehungen</span>
        <span class="text-sm font-normal text-base-content/70">${summary}</span>
      </span>
    `,
    headingActions: canAdd
      ? html`
          <button type="button" class="btn btn-ghost btn-sm" @click=${() => openDialog()}>
            <span class="icon-[material-symbols--add-link-rounded] size-5"></span>
            ${isStudentRelative(self) ? 'Sorgeberechtigten/Spender hinzufügen' : 'Kind hinzufügen'}
          </button>
        `
      : nothing,
    content: () =>
      graph.nodes.length < 2
        ? html`<p class="text-base-content/70">Für diese Person sind noch keine Beziehungen hinterlegt.</p>`
        : html`
            <div class="overflow-x-auto pb-1">
              <div
                class="relative mx-auto"
                style=${styleMap({ width: `${graph.width}px`, height: `${graph.height}px` })}
              >
                <svg
                  class="pointer-events-none absolute left-0 top-0 overflow-visible"
                  width=${graph.width}
                  height=${graph.height}
                  aria-hidden="true"
                >
                  ${graph.lines.map(
                    line =>
                      svg`<path
                        d=${line.path}
                        fill="none"
                        class="[stroke-linecap:round] ${line.touchesSelf ? 'stroke-primary stroke-[2.5]' : 'stroke-base-content/40 stroke-2'} ${line.spender ? '[stroke-dasharray:6_5]' : ''}"
                      ></path>`,
                  )}
                  ${
                    hovered
                      ? svg`<path
                          d=${hovered.path}
                          fill="none"
                          class="stroke-info stroke-[3.5] [stroke-linecap:round] [stroke-linejoin:round] ${hovered.spender ? '[stroke-dasharray:6_5]' : ''}"
                        ></path>`
                      : nothing
                  }
                </svg>
                ${
                  canEdit
                    ? html`
                        <svg
                          class="pointer-events-none absolute left-0 top-0 overflow-visible"
                          width=${graph.width}
                          height=${graph.height}
                        >
                          ${graph.links.map(
                            link =>
                              svg`<path
                                d=${link.path}
                                fill="none"
                                role="button"
                                tabindex="0"
                                aria-label=${`Beziehung ${nameOf(link.parentID)} – ${nameOf(link.childID)} bearbeiten`}
                                class="cursor-pointer stroke-transparent stroke-[16] outline-none [pointer-events:stroke]"
                                @mouseenter=${() => setHoveredLink(linkKey(link))}
                                @mouseleave=${() => setHoveredLink(null)}
                                @focus=${() => setHoveredLink(linkKey(link))}
                                @blur=${() => setHoveredLink(null)}
                                @click=${() => editLink(link)}
                                @keydown=${(event: KeyboardEvent) => {
                                  if (event.key !== 'Enter' && event.key !== ' ') return;
                                  event.preventDefault();
                                  void editLink(link);
                                }}
                              ></path>`,
                          )}
                        </svg>
                      `
                    : nothing
                }
                ${repeat(
                  graph.nodes,
                  node => node.member._id,
                  node => RelationCard({ node, selfAge }),
                )}
              </div>
            </div>
            <div class="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-base-content/70">
              <span class="flex items-center gap-2">
                <svg width="36" height="8" aria-hidden="true">
                  <path d="M 0 4 H 36" fill="none" class="stroke-base-content/40 stroke-2"></path>
                </svg>
                Sorgeberechtigt
              </span>
              <span class="flex items-center gap-2">
                <svg width="36" height="8" aria-hidden="true">
                  <path
                    d="M 0 4 H 36"
                    fill="none"
                    class="stroke-base-content/40 stroke-2 [stroke-dasharray:6_5]"
                  ></path>
                </svg>
                Spender
              </span>
              <span class="flex items-center gap-2">
                <svg width="36" height="8" aria-hidden="true">
                  <path d="M 0 4 H 36" fill="none" class="stroke-primary stroke-[2.5]"></path>
                </svg>
                Verbindung zu ${self.vorname ?? 'dieser Person'}
              </span>
              ${canEdit ? html`<span>Eine Linie anklicken, um die Beziehung zu bearbeiten</span>` : nothing}
            </div>
          `,
  });
});
